const { differenceInCalendarMonths, subYears } = require('date-fns')
const {
  checkForStockReportRateRequirements,
  buildStockReportRateQueryContext,
  buildStockReportRateExportQueryContext,
  buildStockReportRateSummaryQuery,
  buildStockReportRateLocationTableQuery,
  buildStockReportRateCycleByCycleQuery,
  buildStockReportRateProgramCyclesQuery,
  buildStockReportRateExportRowsQuery,
  getStockReportRateLevel
} = require('./operational/stock-report-rate')
const {
  getRevenueSummary: getRevenueSummaryData,
  getRevenueByLocationTable: getRevenueByLocationTableData,
  getRevenueByProductTable: getRevenueByProductTableData,
  getRevenueMissingPricesSummary: getRevenueMissingPricesSummaryData,
  getMonthlyRevenue: getMonthlyRevenueData,
  parseDateOnlyUtcOrThrow
} = require('./financial/revenue')

function normalizeProgramCycleFromOptions (options) {
  if (!options) {
    return null
  }

  const periods = options.report && Array.isArray(options.report.periods) ? options.report.periods : []
  const periodType = periods[0] && periods[0].periodType

  if (periodType && periodType.unit === 'week' && periodType.quantity === 1) {
    return 'week'
  }

  if (periodType && periodType.unit === 'month' && periodType.quantity === 1) {
    return 'month'
  }

  if (
    periodType && periodType.unit === 'month' &&
    (periodType.quantity === 2 || periodType.quantity === 3)
  ) {
    return 'bimonth'
  }

  return null
}

class AnalyticsApi {
  constructor (pgConnection, agaveAdapter) {
    this.pgConnection = pgConnection
    this.agaveAdapter = agaveAdapter
  }

  async _resolveServiceIds (programs) {
    if (!programs || programs.length === 0) return null
    const { rows } = await this.pgConnection.query(
      `SELECT ds.id FROM avocado.data_service ds
       JOIN avocado.data_program dp ON dp.id = ds.program_id
       WHERE dp.fsid = ANY($1)`,
      [programs]
    )
    return rows.map(r => r.id)
  }

  async getRevenueSummary ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getRevenueSummaryData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getRevenueByLocationTable ({ fromDate, toDate, organisationId, location, productSku, programs, aggregationLevel }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getRevenueByLocationTableData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds,
      aggregationLevel
    })
  }

  async getRevenueByProductTable ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getRevenueByProductTableData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getRevenueMissingPricesSummary ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getRevenueMissingPricesSummaryData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getMonthlyRevenue ({ toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getMonthlyRevenueData({
      pgConnection: this.pgConnection,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getStockReportRateSummary ({ fromDate, toDate, programs, country, zone, state, lga, sdp }) {
    checkForStockReportRateRequirements(this.pgConnection, programs)
    const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
    const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
    const toDateExclusiveUtc = new Date(toDateUtc)
    toDateExclusiveUtc.setUTCDate(toDateExclusiveUtc.getUTCDate() + 1)
    const { params, whereClauses } = buildStockReportRateQueryContext({
      programs,
      fromDateUtc,
      toDateExclusiveUtc,
      country,
      zone,
      state,
      lga,
      sdp
    })

    const query = buildStockReportRateSummaryQuery({
      whereClauses: whereClauses
    })

    const { rows: [summary = {}] } = await this.pgConnection.query(query, params)
    const expectedLocations = Number(summary.expected_locations || 0)
    const completedLocations = Number(summary.completed_locations || 0)
    const total = expectedLocations > 0 ? completedLocations / expectedLocations : null

    return {
      fromDate,
      toDate,
      country: country,
      zone: zone,
      state: state,
      lga: lga,
      sdp: sdp,
      programs: params[0],
      total,
      level: getStockReportRateLevel(total),
      completedLocations,
      expectedLocations
    }
  }

  async getStockReportRateLocationTable ({ fromDate, toDate, programs, groupedBy, country, zone, state, lga, sdp }) {
    checkForStockReportRateRequirements(this.pgConnection, programs)
    const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
    const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
    const toDateExclusiveUtc = new Date(toDateUtc)
    toDateExclusiveUtc.setUTCDate(toDateExclusiveUtc.getUTCDate() + 1)
    const { params, whereClauses } = buildStockReportRateQueryContext({
      programs,
      fromDateUtc,
      toDateExclusiveUtc,
      country,
      zone,
      state,
      lga,
      sdp
    })

    const query = buildStockReportRateLocationTableQuery({
      whereClauses: whereClauses,
      groupedBy
    })
    const { rows } = await this.pgConnection.query(query, params)

    const mappedRows = groupedBy === 'sdp'
      ? rows.map((row) => ({
        locationFsid: row.location_fsid,
        sdp: row.sdp,
        completed: row.completed === true,
        numberOfReportsExpected: Number(row.number_of_reports_expected || 0),
        numberOfReportsSubmitted: Number(row.number_of_reports_submitted || 0),
        expectedStockReportIds: Array.isArray(row.expected_stock_report_ids) ? row.expected_stock_report_ids : [],
        submittedStockReportIds: Array.isArray(row.submitted_stock_report_ids) ? row.submitted_stock_report_ids : []
      }))
      : rows.map((row) => {
        const buildGroupedId = () => {
          if (groupedBy === 'zone') {
            return row.zone ? `zone:${row.zone}` : (row.country ? `country:${row.country}` : null)
          }
          if (groupedBy === 'state') {
            if (row.zone) return `zone:${row.zone}:state:${row.state}`
            if (row.country) return `country:${row.country}:state:${row.state}`
            return row.state ? `state:${row.state}` : null
          }
          if (groupedBy === 'lga') {
            if (row.zone) return `zone:${row.zone}:state:${row.state}:lga:${row.lga}`
            if (row.country) return `country:${row.country}:state:${row.state}:lga:${row.lga}`
            return row.state && row.lga ? `state:${row.state}:lga:${row.lga}` : null
          }
          return null
        }
        const expectedLocations = Number(row.total_number_of_locations_expected_to_report || 0)
        const completedLocations = Number(row.number_of_locations_completed || 0)
        const value = expectedLocations > 0
          ? completedLocations / expectedLocations
          : null
        return {
          id: buildGroupedId(),
          groupedByValue: row.grouped_by_value,
          expectedLocations,
          completedLocations,
          value,
          level: getStockReportRateLevel(value)
        }
      })

    return {
      fromDate,
      toDate,
      groupedBy,
      country: country,
      zone: zone,
      state: state,
      lga: lga,
      sdp: sdp,
      programs: params[0],
      rows: mappedRows
    }
  }

  async getStockReportRateCycleByCycle ({ fromDate, toDate, programs, country, zone, state, lga, sdp }) {
    checkForStockReportRateRequirements(this.pgConnection, programs)
    const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
    const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
    const toDateExclusiveUtc = new Date(toDateUtc)
    toDateExclusiveUtc.setUTCDate(toDateExclusiveUtc.getUTCDate() + 1)
    // the logic below that decides the scope for which we query the data is the same as in the legacy ES solution
    // see backend/stacks/internal_api/endpoint/src/stock-report-rate.js
    const isYearOrMore = differenceInCalendarMonths(fromDateUtc, toDateUtc) >= 12
    const startDate = !isYearOrMore ? subYears(fromDateUtc, 1) : fromDateUtc
    const cycleByCycleLimit = 60
    const normalizedPrograms = Array.isArray(programs)
      ? programs.filter(Boolean)
      : [programs].filter(Boolean)

    // When multiple programs are requested, ensure they all share the same
    // reporting cycle. This should never fail for current tenants, but the
    // validation is kept for future compatibility.
    let programsForQuery = programs
    if (normalizedPrograms.length > 1) {
      const query = buildStockReportRateProgramCyclesQuery()
      const { rows = [] } = await this.pgConnection.query(query, [normalizedPrograms])

      const foundProgramIds = new Set(rows.map((row) => row.fsid))
      const missingPrograms = normalizedPrograms.filter((id) => !foundProgramIds.has(id))
      if (missingPrograms.length > 0) {
        console.warn({
          code: 'stock-report-rate-cycle-by-cycle-programs-missing',
          requestedPrograms: normalizedPrograms,
          foundPrograms: Array.from(foundProgramIds),
          missingPrograms
        })
      }

      programsForQuery = normalizedPrograms.filter((id) => foundProgramIds.has(id))
      if (rows.length > 1) {
        const cycles = rows.map((row) =>
          normalizeProgramCycleFromOptions(row.options)
        )

        if (cycles.some((cycle) => cycle == null)) {
          const err = new Error('Unable to determine the reporting cycle for one or more programs.')
          err.status = 400
          throw err
        }

        if (cycles.some((cycle) => cycle !== cycles[0])) {
          const err = new Error('Programs must share the same reporting cycle.')
          err.status = 400
          throw err
        }
      }
    }

    // Assumption: all programs passed in programs param use the same reporting cycle.
    // We aggregate by period_start buckets exactly as stored in analytics.stock_report_rate.
    const { params, whereClauses } = buildStockReportRateQueryContext({
      programs: programsForQuery,
      fromDateUtc: startDate,
      toDateExclusiveUtc,
      country,
      zone,
      state,
      lga,
      sdp
    })
    const query = buildStockReportRateCycleByCycleQuery({
      whereClauses,
      limit: cycleByCycleLimit
    })
    const { rows } = await this.pgConnection.query(query, params)
    const values = rows
      .slice()
      .sort((a, b) => String(a.period_start_date).localeCompare(String(b.period_start_date)))
      .map((row) => {
        const expectedLocations = Number(row.expected_locations || 0)
        const completedLocations = Number(row.completed_locations || 0)
        const ratio = expectedLocations > 0 ? (completedLocations / expectedLocations) : 0
        const cycleStartDate = String(row.period_start_date)
        return {
          value: parseFloat((ratio * 100).toFixed(0)),
          date: `${cycleStartDate}T00:00:00.000Z`,
          tooltip: {
            label: 'SDPs reported',
            value: completedLocations
          }
        }
      })

    return {
      items: [{
        startDate: startDate.toISOString(),
        values,
        endDate: toDateUtc.toISOString(),
        thresholds: {
          // these values are the same as in the legacy ES solution, see backend/stacks/internal_api/endpoint/src/stock-report-rate.js
          // they are kept for backwards compatibility as this has been hardcoded on the ES solution.
          max: 95,
          maxLabel: 'critical'
        },
        unit: '%'
      }]
    }
  }

  async getStockReportRateExportRows ({ fromDate, toDate, location, programs, funders, limit, offset }) {
    checkForStockReportRateRequirements(this.pgConnection, programs)
    const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
    const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
    const { params, whereClauses, limitParam, offsetParam } = buildStockReportRateExportQueryContext({
      programs,
      fromDateUtc,
      toDateUtc,
      location,
      funders,
      limit,
      offset
    })
    const query = buildStockReportRateExportRowsQuery({ whereClauses, limitParam, offsetParam })
    const { rows = [] } = await this.pgConnection.query(query, params)
    const mappedRows = rows.map((row) => ({
      _source: {
        date: row.date,
        location: {
          id: row.location_id,
          zone: row.zone,
          state: row.state,
          lga: row.lga,
          sdp: row.sdp,
          warehouseCode: row.warehouse_code
        },
        owner: {
          program: row.program_fsid,
          service: row.service_fsid,
          funder: row.funder
        },
        value: row.submitted === true,
        submissionDate: row.submitted_at ? new Date(row.submitted_at).toISOString() : null
      }
    }))

    const resolvedLimit = Number(limit) || 5000
    const resolvedOffset = Number(offset) || 0
    return {
      rows: mappedRows,
      nextOffset: rows.length === resolvedLimit ? (resolvedOffset + resolvedLimit) : null
    }
  }

  fetchRevenueSummary (params) {
    return this.agaveAdapter.get('analytics/revenue/summary', params)
  }

  fetchRevenueByLocationTable (params) {
    return this.agaveAdapter.get('analytics/revenue/location-table', params)
  }

  fetchRevenueByProductTable (params) {
    return this.agaveAdapter.get('analytics/revenue/product-table', params)
  }

  fetchRevenueMissingPricesSummary (params) {
    return this.agaveAdapter.get('analytics/revenue/missing-prices-summary', params)
  }

  fetchMonthlyRevenue (params) {
    return this.agaveAdapter.get('analytics/revenue/monthly-revenue', params)
  }

  fetchStockReportRateSummary (params) {
    return this.agaveAdapter.get('analytics/stock-report-rate/summary', params)
  }

  fetchStockReportRateLocationTable (params) {
    return this.agaveAdapter.get('analytics/stock-report-rate/location-table', params)
  }

  fetchStockReportRateCycleByCycle (params) {
    return this.agaveAdapter.get('analytics/stock-report-rate/cycle-by-cycle', params)
  }

  fetchStockReportRateExportRows (params) {
    return this.agaveAdapter.get('analytics/stock-report-rate/export-rows', params)
  }
}

module.exports = AnalyticsApi
